/* global bootstrap */

import React, { useContext, useState, useRef } from "react";
import LanguageContext from "../utils/Contexts/LanguageContext";
import axiosInstance from "../utils/Requests/axios";
import UserContext from "../utils/Contexts/UserContext";
import ThemeContext from "../utils/Contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

function ExportWebsiteModal({ setShouldUpdate }) {
  const { t } = useContext(LanguageContext);
  const { selectedTheme } = useContext(ThemeContext);
  const [exportType, setExportType] = useState("offers"); // default to 'offers'
  const [status, setStatus] = useState(""); // default to 'any'
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const modalRef = useRef(null);

  const handleExportStart = () => {
    let url = "";
    if (exportType === "offers") {
      url =
        status === ""
          ? `/exports/products/100/sellers/${userData?.uuid}/offers`
          : `/exports/products/100/sellers/${userData?.uuid}/offers?status=${status}`;
    } else if (exportType === "terms") {
      url =
        status === ""
          ? `/exports/products/100/sellers/${userData?.uuid}/terms`
          : `/exports/products/100/sellers/${userData?.uuid}/terms?status=${status}`;
    }

    axiosInstance
      .post(url)
      .then(() => {
        const modal = bootstrap.Modal.getInstance(modalRef.current);
        modal.hide();
        navigate("/offers/export");
        setShouldUpdate(true);
      })
      .catch(() => {
        toast.error(t(`Failed to export ${exportType}.`));
      });
  };

  const getRadioLabelStyle = (isSelected) => {
    return { color: isSelected ? (selectedTheme === "dark" ? "#dbdcdc" : "#1e2022") : "#65686a" };
  };

  return (
    <div
      ref={modalRef}
      className="modal fade exportModal"
      id="exportModal"
      tabIndex="-1"
      aria-labelledby="exportModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered w-200">
        <div className="modal-content">
          <div className="modal-header">
            <h3 className="modal-title" id="exportModalLabel">
              {t("Export Offers or Website Terms")}
            </h3>
          </div>

          <div className="modal-body pt-2 pb-0">
            <p style={{ color: "#65686a" }}>
              {t(
                "For a large inventory of offers, the export process may take some time. After initiating the export, you will be redirected to the Export page, where you can track the status of the process. Additionally, once the export is complete, you will receive a link to download the export file."
              )}
            </p>
            <div
              className="d-flex justify-content-between pt-3"
              style={{
                borderTop: `1px solid ${selectedTheme === "dark" ? '#2f3235' : 'rgba(231, 234, 243, 0.7)'}`,
                borderBottom: `1px solid ${selectedTheme === "dark" ? '#2f3235' : 'rgba(231, 234, 243, 0.7)'}`
              }}
            >
              <div>
                <label className="form-label">{t("Export")}:</label>

                <div className="d-grid gap-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="exportType"
                      id="exportRadio11"
                      checked={exportType === "offers"}
                      onChange={() => setExportType("offers")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exportRadio11"
                      style={getRadioLabelStyle(exportType === "offers")}
                    >
                      {t("Offers")}
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="exportType"
                      id="exportRadio12"
                      checked={exportType === "terms"}
                      onChange={() => setExportType("terms")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exportRadio12"
                      style={getRadioLabelStyle(exportType === "terms")}
                    >
                      {t("Website Terms")}
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">{t("With Status:")}</label>

                <div className="d-grid gap-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="exportRadio"
                      id="exportRadio1"
                      checked={status === ""}
                      onChange={() => setStatus("")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exportRadio1"
                      style={getRadioLabelStyle(status === "")}
                    >
                      {t("All")}
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="exportRadio"
                      id="exportRadio2"
                      checked={status === "2"}
                      onChange={() => setStatus("2")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exportRadio2"
                      style={getRadioLabelStyle(status === "2")}
                    >
                      {t("Approved")}
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="exportRadio"
                      id="exportRadio3"
                      checked={status === "0"}
                      onChange={() => setStatus("0")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exportRadio3"
                      style={getRadioLabelStyle(status === "0")}
                    >
                      {t("Pending Review")}
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="exportRadio"
                      id="exportRadio4"
                      checked={status === "1"}
                      onChange={() => setStatus("1")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exportRadio4"
                      style={getRadioLabelStyle(status === "1")}
                    >
                      {t("Declined")}
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="form-label">{t("Export as")}:</label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="exportProductsAsRadio"
                    id="exportProductsAsRadio2"
                    checked
                  />
                  <label
                    className="form-check-label"
                    htmlFor="exportProductsAsRadio2"
                    style={{ color: selectedTheme === "dark" ? "#dbdcdc" : "#1e2022" }}
                  >
                    {t("Plain CSV file")}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer py-3" style={{ border: "unset" }}>
            <button
              type="button"
              className="btn btn-outline-primary px-6 py-2"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary px-5 py-2"
              onClick={handleExportStart}
            >
              {t("Start Export")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportWebsiteModal;
